import React, { useState, useEffect, SyntheticEvent, useMemo, useRef } from 'react';
import {
  useTheme, useMediaQuery, Typography, CardActions, TextField, Button, Box, Card, Grid, FormControl, MenuItem, InputLabel, Select, CardContent, Divider,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import axios from 'axios';
import { NavLink, useNavigate } from 'react-router-dom';
import Title from '../../../components/Title';
import { Collect } from '../../../models/collect';
import { Client } from '../../../models/client';
import { Constant } from '../../../models/constant';
import { Office } from '../../../models/office';
import useAxiosInterceptor from '../../AxiosInterceptor';
import moment from 'moment-timezone';
import { useSelection } from '../../../components/context/SelectionContext';
import { ClientWithLends } from '../../../models/clientLends';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

/** Alineado con CreateLend (API): con `enable_quote_client` en el cobro, tope = cupo cliente; si no, cupo del cobro. */
const effectiveLoanQuote = (
  enableQuoteClient: boolean,
  clientQuote: unknown,
  collectQuote: unknown
): number => {
  const cq = Number(clientQuote) || 0;
  const oq = Number(collectQuote) || 0;
  return enableQuoteClient ? cq : oq;
};

const getPeriodCount = (termInDays: number, typeConstantId?: number | null): number => {
  if (!Number.isFinite(termInDays) || termInDays <= 0) return 1;
  if (typeConstantId === 2) return Math.max(1, Math.round((termInDays / 30) * 4));
  if (typeConstantId === 3) return Math.max(1, Math.round(termInDays / 15));
  if (typeConstantId === 4) return Math.max(1, Math.round(termInDays / 30));
  return termInDays;
};

const AbonosAdd: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  useAxiosInterceptor();
  document.title = "Crear Préstamo - Mis Rutas";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [client_id, setClient_id] = useState<number | string>('');
  const [value, setValue] = useState("");
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [days_term, setDays_term] = useState("");
  const [utility, setUtility] = useState("");
  const [position, setPosition] = useState("");
  const [quote, setQuote] = useState<number>(0);
  const [collectClient, setCollectClient] = useState<Client[]>([]);
  const [positions, setPositions] = useState<ClientWithLends[]>([]);

  const [constants, setConstants] = useState<Constant[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [collects, setCollects] = useState<Collect[]>([]);
  const [insurance_charge, setInsuranceCharge] = useState<number>(0);
  const [insuranceChargeFocused, setInsuranceChargeFocused] = useState(false);
  const [Constantid, setConstantid] = useState('');
  const [total_value, setTotal_Value] = useState<number>(0);
  const [daily_pay_value, setDaily_pay_value] = useState<number>(0);
  const [location, setLocation] = useState<string | null>(null);
  const { office_id, setOffice_id, collect_id, setCollect_id, user_id, setUser_id } = useSelection();
  const { canAllowHistoricalLendDate } = useResourcePermissions('/prestamos/abonos/list');
  const [isWeekly, setIsWeekly] = useState(false);
  const [selectedTypeConstantId, setSelectedTypeConstantId] = useState<number | null>(null);
  const [routeDay, setRouteDay] = useState<string>('');
  const todayDate = moment().tz('America/Bogota').format('YYYY-MM-DD');
  const allowedOfficeIds = useMemo(
    () => new Set(offices.map((office) => Number(office.id))),
    [offices]
  );
  const prevCollectIdForInsurance = useRef<string | number | null>(null);

  const [currentDate, setCurrentDate] = useState(() => {
    return moment().tz('America/Bogota').format('YYYY-MM-DD'); // valor inicial: hoy
  });

  useEffect(() => {
    if (!canAllowHistoricalLendDate && currentDate < todayDate) {
      setCurrentDate(todayDate);
    }
  }, [canAllowHistoricalLendDate, currentDate, todayDate]);

  useEffect(() => {
    const fetchData = async () => {
      showLoading();
      try {
        const [
          { data: userData },
          { data: collectsData },
          { data: officesData },
          { data: clientsData },
        ] = await Promise.all([
          axios.get("user"),
          axios.get("collects"),
          axios.get("offices"),
          axios.get("clients"),
        ]);
        const officePayload = Array.isArray(officesData?.data)
          ? officesData.data
          : Array.isArray(officesData)
            ? officesData
            : [];
        setUser_id(userData.data.id);
        setCollects(collectsData.data);
        setOffices(officePayload);
        setClients(clientsData.data);
        await new Promise(resolve => setTimeout(resolve, 2000)); // Esperar 2 segundos
      } catch (error) {
        showAlert('Error fetching data!', 'error');
      } finally {
        hideLoading();
      }
    };
    fetchData();
  }, []);
  useEffect(() => {
    const fetchCoordinates = async () => {
      try {
        const coords = await getCoordinates();
        setLocation(coords);
      } catch (error) {
        showAlert('No se puede registrar sin activar el GPS.', 'error');
      }
    };

    fetchCoordinates();
  }, []); // ✅ solo se ejecuta al montar el componente

  useEffect(() => {
    if (office_id && allowedOfficeIds.has(Number(office_id))) {
      const fetchConstants = async () => {
        showLoading();
        try {
          const { data } = await axios.get(`constantofficescomplete/${office_id}`);
          setConstants(data.constants)
        } catch (error) {
          showAlert('No se encontraron constantes asociadas a la oficina!', 'warning');
        } finally {
          hideLoading();
        }
      };

      fetchConstants();
    } else {
      setConstants([]);
      setConstantid('');
    }
  }, [office_id, allowedOfficeIds]);

  useEffect(() => {
    const collectAllowed = collectsOffice.some((collect: Collect) => collect.id === Number(collect_id));
    if (collect_id && collectAllowed) {
      showLoading();
      (async () => {
        try {
          const { data } = await axios.get(`collectclient/${collect_id}`);
          setCollectClient(data.data);
          setPositions(data.data);
          //console.log("Collect Client Data:", data.data);
          if (data.data.length === 0) {
            showAlert('No se encuentran clientes en el cobro!', 'warning');
          }
        } catch (error) {
          showAlert('Error fetching data!', 'error');
        } finally {
          hideLoading();
        }
      })();
    } else {
      setCollectClient([]);
      setPositions([]);
    }
  }, [collect_id, collectsOffice]);

  useEffect(() => {
    if (office_id && allowedOfficeIds.has(Number(office_id))) {
      (async () => {
        try {
          const { data } = await axios.get(`collectsoffice/${office_id}`);
          setCollectsOffice(data);
          if (!data.some((collect: Collect) => collect.id === Number(collect_id))) {
            setCollect_id(""); // Reset collect_id if it's not in the new list
          }
        } catch (error) {
          showAlert('Error fetching data!', 'error');
        }
      })();
    } else {
      setCollectsOffice([]);
      setCollect_id("");
    }
  }, [office_id, allowedOfficeIds]);

  useEffect(() => {
    const selectedCollect = collectsOffice.find((collect: Collect) => collect.id === Number(collect_id));
    const collectQuote = selectedCollect ? Number(selectedCollect.quote) || 0 : 0;
    const enableQuoteClient = Boolean(
      Number((selectedCollect as Collect | undefined)?.enable_quote_client ?? 0)
    );
    if (!collect_id || !selectedCollect) {
      setQuote(0);
      return;
    }
    if (!client_id) {
      setQuote(collectQuote);
      return;
    }
    const selectedClient = collectClient.find((c: Client) => Number(c.id) === Number(client_id));
    if (!selectedClient) {
      setQuote(collectQuote);
      return;
    }
    setQuote(effectiveLoanQuote(enableQuoteClient, selectedClient.quote, collectQuote));
  }, [collect_id, collectsOffice, client_id, collectClient]);

  useEffect(() => {
    const selectedCollect = collectsOffice.find((collect: Collect) => collect.id === Number(collect_id));
    const requestedValue = parseFloat(value) || 0;
    const collectChanged = String(collect_id) !== String(prevCollectIdForInsurance.current);
    prevCollectIdForInsurance.current = collect_id;

    if (!selectedCollect) {
      setInsuranceCharge(0);
      return;
    }

    if (collectChanged) {
      setInsuranceCharge(0);
    }

    if (selectedCollect.cons_insurance_manual) {
      if (requestedValue <= 0) {
        setInsuranceCharge(0);
      }
      return;
    }

    if (requestedValue <= 0) {
      setInsuranceCharge(0);
      return;
    }

    const insuranceValue = Number(selectedCollect.insurance ?? 0);
    const consInsuranceValue = Number(selectedCollect.cons_insurance ?? 0);

    if (insuranceValue <= 0 || consInsuranceValue <= 0) {
      setInsuranceCharge(0);
      return;
    }

    setInsuranceCharge(Math.ceil((requestedValue / consInsuranceValue) * insuranceValue));
  }, [collect_id, collectsOffice, value]);

  useEffect(() => {
    const parsedValue = parseFloat(value) || 0;
    const parsedUtility = parseFloat(utility) || 0;
    const parsedTerm = parseInt(days_term) || 1;
    const periodCount = getPeriodCount(parsedTerm, selectedTypeConstantId);

    const totalValue = parsedValue + (parsedValue * parsedUtility / 100);
    const dailyPayValue = Math.round(totalValue / periodCount);

    setTotal_Value(totalValue);
    setDaily_pay_value(dailyPayValue);
  }, [value, days_term, utility, selectedTypeConstantId]);

  const paymentCount = getPeriodCount(parseInt(days_term) || 0, selectedTypeConstantId);


  const getCoordinates = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        showAlert('Geolocation is not supported by this browser.', 'warning');
        return reject(new Error('Geolocation not supported'));
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const coordinates = `${lat}, ${lng}`;
          resolve(coordinates);
        },
        (error) => {
          showAlert('Error obteniendo ubicación: ' + error.message, 'error');
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,         // ⏱️ máximo 5 segundos
          maximumAge: 0          // no usar ubicación cacheada
        }
      );
    });
  };



  const submit = async (event: SyntheticEvent) => {
    event.preventDefault(); // Evita el envío del formulario por defecto
    if (!location) {
      showAlert('Debe activar el GPS para realizar el registro.', 'error');
      return;
    }
    if (!collect_id || !client_id || !Constantid || !value || !position || !currentDate) {
      showAlert('Tiene campos pendientes por diligenciar!', 'warning');
      return;
    }
    if (parseFloat(value) > quote) {
      showAlert('El valor solicitado debe ser menor o Igual al cupo del cobro!', 'error');
      return;
    }
    if (currentDate > todayDate) {
      showAlert('La fecha del préstamo no puede ser posterior a hoy.', 'error');
      return;
    }
    if (!canAllowHistoricalLendDate && currentDate < todayDate) {
      showAlert('No tienes permiso para registrar una fecha anterior a hoy.', 'error');
      return;
    }
    if (isWeekly && (routeDay === '' || routeDay === undefined)) {
      showAlert('Seleccione el día de cobro para la constante semanal.', 'warning');
      return;
    }

    const formData = {
      collect_id,
      client_id,
      constant_id: Constantid,
      value,
      utility,
      user_id,
      days_term: paymentCount,
      position,
      date: currentDate,
      total_value,
      daily_pay_value,
      insurance_charge,
      location,
      route_day: isWeekly ? Number(routeDay) : null,
    };
    const url = "/lends";
    try {
      showLoading();
      const response = await axios.post(url, formData);
      showAlert('Se ha realizado el registro exitosamente!', 'success');
      await new Promise(resolve => setTimeout(resolve, 1000));
      navigate("/prestamos/abonos/list");
    } catch (error) {
      showAlert('Error fetching data!', 'error');
    } finally {
      hideLoading();
    }
  };

  const handleUtilityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let next = event.target.value;
    if (next.length > 3) {
      next = next.slice(0, 3);
    }
    const num = next === '' ? NaN : Number(next);
    if (next !== '' && !Number.isNaN(num) && num > 100) {
      next = next.slice(0, -1);
    }
    setUtility(next);
  };
  const lendPositions = useMemo(
    () =>
      positions
        .flatMap((client) => {
          if (!client.lend || client.lend.length === 0) {
            return [];
          }
          return client.lend
            .filter((lend) => lend.state === 0 && lend.position !== null && lend.position !== undefined)
            .map((lend) => ({
              key: `${client.id}-${lend.id}`,
              position: lend.position,
              clientName: client.fullname,
            }));
        })
        .sort((a, b) => a.position - b.position),
    [positions]
  );

  const positionOptions = useMemo(
    () =>
      lendPositions.length > 0
        ? lendPositions
        : [{ key: 'default-position-1', position: 1, clientName: '' }],
    [lendPositions]
  );

  useEffect(() => {
    if (!collect_id) {
      setPosition('');
      return;
    }

    const hasCurrentPosition = positionOptions.some(
      (item) => String(item.position) === String(position)
    );

    if (!position || !hasCurrentPosition) {
      setPosition(String(positionOptions[0]?.position ?? '1'));
    }
  }, [collect_id, position, positionOptions]);

  return (
    <HeaderResponsive>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Title>Registrar Nuevo Préstamo</Title>
      </Box>
      <Card>
        <Divider sx={{ margin: 0 }} />
        <form onSubmit={submit}>
          <CardContent>
             <Grid container spacing={2}>
              <Grid item xs={12} sm={6} xl={6}>
                <FormControl fullWidth>
                  <InputLabel id="form-layouts-separator-select-label">
                    Seleccionar Oficina
                  </InputLabel>
                  {offices && offices.length > 0 ? (
                    <Select
                      label="Seleccionar Oficina"
                      size="small"
                      value={office_id}
                      id="form-layouts-separator-select"
                      labelId="form-layouts-separator-select-label"
                      onChange={(event) => setOffice_id(event.target.value)}
                      required
                    >
                      {offices.map((office: Office) => {
                        return (
                          <MenuItem key={office.id} value={office.id}>
                            {office.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  ) : null}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} xl={6}>
                <FormControl fullWidth>
                  <InputLabel id="collect">Seleccionar Cobro</InputLabel>
                  {collectsOffice && collectsOffice.length > 0 ? (
                    <Select
                      label="Seleccionar Cobro"
                      size="small"
                      value={collect_id}
                      id="collects_id"
                      labelId="collects_id"
                      onChange={(event) => setCollect_id(event.target.value)}
                      required
                    >
                      {collectsOffice.map((collect: Collect) => (
                        <MenuItem key={collect.id} value={collect.id}>
                          {collect.name}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : null}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} xl={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Cupo disponible"
                  type="number"
                  value={quote}
                  inputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="form-layouts-separator-select-label">
                    Seleccionar Cliente
                  </InputLabel>
                  {collectClient && collectClient.length > 0 ? (
                    <Select
                      label="seleccionar cliente"
                      value={client_id}
                      size="small"
                      id="form-layouts-separator-select"
                      labelId="seleccionar cliente"
                      onChange={(event) =>
                        setClient_id(event.target.value)
                      }
                      required
                    >
                      {collectClient.map((client: Client) => {
                        return (
                          <MenuItem key={client.id} value={client.id}>
                            {client.fullname}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  ) : null}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="position-select-label">Seleccionar Posición</InputLabel>
                  <Select
                    labelId="position-select-label"
                    id="position"
                    value={position}
                    label="Seleccionar Posición"
                    onChange={(event) => setPosition(event.target.value)}
                    size="small"
                    required
                  >
                    {positionOptions.map((lend) => (
                      <MenuItem key={lend.key} value={String(lend.position)}>
                        {lend.clientName
                          ? `${lend.position} — ${lend.clientName}`
                          : `${lend.position}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  value={currentDate}
                  size="small"
                  onChange={(e) => setCurrentDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={
                    canAllowHistoricalLendDate
                      ? { max: todayDate }
                      : {
                          max: todayDate,
                          min: todayDate,
                        }
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Gasto Gestión"
                  type="number"
                  value={
                    collectsOffice.find((collect: Collect) => collect.id === Number(collect_id))?.cons_insurance_manual &&
                    insuranceChargeFocused &&
                    Number(insurance_charge ?? 0) === 0
                      ? ''
                      : insurance_charge
                  }
                  onChange={(e) => setInsuranceCharge(Number(e.target.value) || 0)}
                  onFocus={() => setInsuranceChargeFocused(true)}
                  onBlur={(e) => {
                    setInsuranceChargeFocused(false);
                    if (!e.target.value.trim()) {
                      setInsuranceCharge(0);
                    }
                  }}
                  inputProps={{ min: 0, readOnly: !collectsOffice.find((collect: Collect) => collect.id === Number(collect_id))?.cons_insurance_manual }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="constante-select-label">
                    Seleccionar Constante *
                  </InputLabel>
                  {constants && constants.length > 0 ? (
                    <Select
                      label="Seleccionar Constante *"
                      value={Constantid}
                      size="small"
                      id="constante_id"
                      labelId="constante-select-label"
                      onChange={(event) => {
                        const selectedConstantId = event.target.value;
                        setConstantid(selectedConstantId);

                        const selectedConstant = constants.find(
                          (constant: Constant) => constant.id === Number(selectedConstantId)
                        );

                        if (selectedConstant) {
                          setDays_term(selectedConstant.term.toString());
                          setUtility(selectedConstant.utility.toString());
                          setSelectedTypeConstantId(Number(selectedConstant.type_constant.id));
                          setIsWeekly(selectedConstant.type_constant.id === 2); // <-- aquí detectamos si es semanal
                        }
                      }}

                      required
                      displayEmpty
                      renderValue={(selected) => {
                        if (selected === '' || selected == null) {
                          return '\u00a0';
                        }
                        const c = constants.find(
                          (constant: Constant) =>
                            String(constant.id) === String(selected)
                        );
                        return c?.name ?? '';
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Seleccione constante</em>
                      </MenuItem>
                      {constants.map((constant: Constant) => (
                        <MenuItem key={constant.id} value={constant.id}>
                          {constant.name}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : null}
                </FormControl>
              </Grid>
              {isWeekly && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="route-day-label">Seleccionar Día</InputLabel>
                    <Select
                      labelId="route-day-label"
                      id="route_day"
                      value={routeDay}
                      label="Seleccionar Día"
                      onChange={(event) => setRouteDay(event.target.value)}
                      size="small"
                      required
                    >
                      <MenuItem value="1">Lunes</MenuItem>
                      <MenuItem value="2">Martes</MenuItem>
                      <MenuItem value="3">Miércoles</MenuItem>
                      <MenuItem value="4">Jueves</MenuItem>
                      <MenuItem value="5">Viernes</MenuItem>
                      <MenuItem value="6">Sábado</MenuItem>
                      <MenuItem value="0">Domingo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Número de Cuotas"
                  value={paymentCount}
                  type="number"
                  inputProps={{ maxLength: 3, readOnly: true, }}
                  onChange={(event) => setDays_term(event.target.value)}
                ></TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Valor Solicitado"
                  type="number"
                  value={value}
                  inputProps={{ maxLength: 10 }}
                  onChange={(event) => setValue(event.target.value)}
                ></TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="% Utilidad"
                  value={utility}
                  type="number"
                  inputProps={{ min: 0, max: 100, maxLength: 3, step: 1, readOnly: true, }}
                  onChange={handleUtilityChange}
                ></TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  value={total_value || 0}
                  label="Valor Total a Pagar"
                  type="number"
                  InputProps={{
                    readOnly: true,
                  }}
                  onChange={(event) => setValue(event.target.value)}
                ></TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  value={daily_pay_value || 0}
                  label="Valor Periodo"
                  InputProps={{
                    readOnly: true,
                  }}
                  onChange={(event) => setValue(event.target.value)}
                ></TextField>
              </Grid>
            </Grid>
          </CardContent>
          <Divider sx={{ margin: 0 }} />
          <CardActions>
            <Button
              size='small'
              type="submit"
              sx={{ mr: 2 }}
              variant="contained"
            >
              Guardar
            </Button>
            <NavLink to="/prestamos/abonos/list">
              <Button size='small' color="secondary" variant="outlined">
                Cancelar
              </Button>
            </NavLink>
          </CardActions>
        </form>
      </Card>
    </HeaderResponsive >
  );
};

export default AbonosAdd;
